import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateTime, formatNumber } from '@/lib/format'
import { scheduledStartToUtc } from '@/lib/dates'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { NoSenderWarning } from '@/components/NoSenderWarning'
import { EnrollStartField, type EnrollStartMode } from '@/components/EnrollStartField'
import { ContactPreviewPanel, type PreviewableContact } from '@/components/ContactPreviewPanel'
import { SegmentContactPicker } from '@/components/SegmentContactPicker'
import { EnrollScreeningGroups } from '@/components/EnrollScreeningGroups'
import { EnrollNotesProgress } from '@/components/EnrollNotesProgress'
import { pruneEmptyOverrides } from '@/lib/overrides'
import { AlertTriangle, CheckCircle, Loader2, RefreshCw } from 'lucide-react'
import {
  useSegments,
  useSequences,
  usePreviewSegmentEnrollment,
  useCreateSegmentEnrollment,
  useSyncContacts,
} from '@/hooks/useApi'
import { useSegmentContactSearch } from '@/hooks/useSegmentContactSearch'
import type { EnrollOverridesMap, SegmentEnrollmentPreview } from '@/types/api'

type Step = 'select' | 'preview' | 'result'

// Contacts left out unless the user chooses "include all"
const needsConfirmation = (preview: SegmentEnrollmentPreview) =>
  preview.warnings.length > 0 ||
  (preview.known_bounces?.length ?? 0) > 0 ||
  (preview.hubspot_hard_bounces?.length ?? 0) > 0

interface EnrollSegmentDialogProps {
  open: boolean
  onClose: () => void
  preselectedSequenceId?: string
}

export function EnrollSegmentDialog({ open, onClose, preselectedSequenceId }: EnrollSegmentDialogProps) {
  const { t } = useTranslation(['sequences', 'common'])
  const [step, setStep] = useState<Step>('select')
  const [selectedSegmentId, setSelectedSegmentId] = useState('')
  const [selectedSequenceId, setSelectedSequenceId] = useState(preselectedSequenceId || '')
  const [startMode, setStartMode] = useState<EnrollStartMode>('now')
  const [startAtLocal, setStartAtLocal] = useState('')
  const [preview, setPreview] = useState<SegmentEnrollmentPreview | null>(null)
  const [enrollResult, setEnrollResult] = useState<{
    enrolled: number
    skipped: number
    skippedDoNotContact: number
    skippedInvalidDomain: number
    batchId: string | null
  } | null>(null)
  // Il contatto aperto non dipende dalla pagina: cambiarla non chiude il pannello
  const [previewContact, setPreviewContact] = useState<PreviewableContact | null>(null)
  const [overridesByContact, setOverridesByContact] = useState<EnrollOverridesMap>({})

  const { data: segments } = useSegments()
  const segmentItems = segments?.items ?? []
  const { data: sequences } = useSequences()
  // Il drill-down vive solo nello step preview: niente fetch anticipato
  const contactSearch = useSegmentContactSearch(selectedSegmentId, step === 'preview' && !!selectedSegmentId)
  const previewMutation = usePreviewSegmentEnrollment()
  const enrollMutation = useCreateSegmentEnrollment()
  const syncMutation = useSyncContacts()

  const activeSequences = sequences?.filter((s) => s.status === 'active') || []
  const selectedSequence = sequences?.find((s) => s.id === selectedSequenceId)
  const sequenceHasNoSender = !!selectedSequence && !selectedSequence.email_account_id

  // Le personalizzazioni valgono per i contatti del segmento e gli step della sequenza
  const forgetContactWork = () => {
    contactSearch.reset()
    setOverridesByContact({})
  }

  const handleSegmentChange = (segmentId: string) => {
    if (segmentId === selectedSegmentId) return
    forgetContactWork()
    setSelectedSegmentId(segmentId)
  }

  const handleSequenceChange = (sequenceId: string) => {
    if (sequenceId === selectedSequenceId) return
    forgetContactWork()
    setSelectedSequenceId(sequenceId)
  }

  const handlePreview = () => {
    if (!selectedSegmentId || !selectedSequenceId) return
    previewMutation.mutate(
      { segment_id: selectedSegmentId, sequence_id: selectedSequenceId },
      {
        onSuccess: (data) => {
          // Older backends may omit `warnings`/`missing_variables` and the
          // checks of #386: normalize so the preview step never crashes
          const warnings = data.warnings ?? []
          setPreview({
            ...data,
            warnings,
            missing_variables: data.missing_variables ?? [],
            invalid_domains: data.invalid_domains ?? [],
            known_bounces: data.known_bounces ?? [],
            hubspot_hard_bounces: data.hubspot_hard_bounces ?? [],
            ready_with_force: data.ready_with_force ?? data.ready + warnings.length,
          })
          setStep('preview')
        },
      }
    )
  }

  const handleSync = () => {
    const seg = segmentItems.find((s) => s.id === selectedSegmentId)
    if (!seg) return
    syncMutation.mutate(
      { mode: 'segment', segment_id: seg.hubspot_segment_id },
      {
        onSuccess: () => {
          // Re-run preview after sync
          handlePreview()
        },
      }
    )
  }

  const handleEnroll = (force: boolean) => {
    if (!selectedSegmentId || !selectedSequenceId) return
    const start_at = scheduledStartToUtc(startMode, startAtLocal, selectedSequence?.timezone)
    const overrides = pruneEmptyOverrides(overridesByContact)
    enrollMutation.mutate(
      {
        segment_id: selectedSegmentId,
        sequence_id: selectedSequenceId,
        force,
        start_at,
        ...(overrides ? { overrides } : {}),
      },
      {
        onSuccess: (data) => {
          setEnrollResult({
            enrolled: data.enrolled,
            skipped: data.skipped,
            skippedDoNotContact: data.skipped_do_not_contact,
            skippedInvalidDomain: data.skipped_invalid_domain ?? 0,
            batchId: data.batch_id ?? null,
          })
          setStep('result')
        },
      }
    )
  }

  const handleClose = () => {
    setStep('select')
    setSelectedSegmentId('')
    setSelectedSequenceId(preselectedSequenceId || '')
    setStartMode('now')
    setStartAtLocal('')
    setPreview(null)
    setEnrollResult(null)
    setPreviewContact(null)
    forgetContactWork()
    previewMutation.reset()
    enrollMutation.reset()
    onClose()
  }

  const formatDate = (iso: string) =>
    formatDateTime(iso, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent className={`max-h-[90vh] overflow-y-auto ${previewContact ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`}>
        {step === 'select' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('enrollDialog.select.title')}</DialogTitle>
              <DialogDescription>
                {t('enrollDialog.select.description')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">{t('enrollDialog.select.howItWorks')}</p>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('enrollDialog.select.segment')}</label>
                <Select value={selectedSegmentId} onValueChange={handleSegmentChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('enrollDialog.select.selectSegment')} />
                  </SelectTrigger>
                  <SelectContent>
                    {segmentItems.map((seg) => (
                      <SelectItem key={seg.id} value={seg.id}>
                        {seg.name} ({seg.contact_count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!preselectedSequenceId && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('enrollDialog.select.sequence')}</label>
                  <SearchableSelect
                    value={selectedSequenceId}
                    onValueChange={handleSequenceChange}
                    options={activeSequences.map((seq) => ({ value: seq.id, label: seq.name }))}
                    placeholder={t('enrollDialog.select.selectSequence')}
                    searchPlaceholder={t('enrollDialog.select.searchSequence')}
                    emptyText={t('enrollDialog.select.noSequencesFound')}
                  />
                </div>
              )}
              {sequenceHasNoSender && <NoSenderWarning />}
              {selectedSequence && (
                <EnrollStartField
                  mode={startMode}
                  onModeChange={setStartMode}
                  value={startAtLocal}
                  onValueChange={setStartAtLocal}
                  timezone={selectedSequence.timezone}
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                {t('common:cancel')}
              </Button>
              <Button
                onClick={handlePreview}
                disabled={!selectedSegmentId || !selectedSequenceId || previewMutation.isPending || (startMode === 'scheduled' && !startAtLocal)}
              >
                {previewMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('enrollDialog.select.loading')}</>
                ) : (
                  t('enrollDialog.select.preview')
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'preview' && preview && previewContact && (
          <ContactPreviewPanel
            sequenceId={selectedSequenceId}
            contact={{
              id: previewContact.id,
              name: previewContact.full_name || previewContact.email,
              email: previewContact.email,
            }}
            draftOverrides={overridesByContact[previewContact.id] ?? {}}
            onDraftChange={(next) =>
              setOverridesByContact((prev) => ({ ...prev, [previewContact.id]: next }))
            }
            onBack={() => setPreviewContact(null)}
          />
        )}

        {step === 'preview' && preview && !previewContact && (
          <>
            <DialogHeader>
              <DialogTitle>{t('enrollDialog.preview.title')}</DialogTitle>
              <DialogDescription>
                {t('enrollDialog.preview.description', { name: preview.segment_name, size: preview.segment_size })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              {/* Sequence without sender: sends would stay blocked */}
              {sequenceHasNoSender && <NoSenderWarning />}

              {/* Smart sync banner */}
              {preview.sync_suggested && (
                <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      {preview.last_synced_at
                        ? t('enrollDialog.preview.syncedAt', { date: formatDate(preview.last_synced_at) })
                        : t('enrollDialog.preview.neverSynced')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSync}
                      disabled={syncMutation.isPending}
                    >
                      {syncMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-1" />
                      )}
                      {t('enrollDialog.preview.sync')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Ready count */}
              <p className="text-sm">
                {t('enrollDialog.preview.ready', { count: preview.ready })}
              </p>

              {/* Skipped */}
              {preview.skipped_active > 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('enrollDialog.preview.skippedActive', { count: preview.skipped_active })}
                </p>
              )}

              {/* Marked "do not contact" (#372): left out whatever is chosen below */}
              {preview.skipped_do_not_contact > 0 && (
                <p className="text-sm text-red-700 dark:text-red-400">
                  {t('enrollDialog.preview.skippedDoNotContact', { count: preview.skipped_do_not_contact })}
                </p>
              )}

              {/* Impact warning */}
              {preview.ready > 100 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {t('enrollDialog.preview.impactWarning', { total: formatNumber(preview.total_emails_estimate) })}
                  </p>
                </div>
              )}

              {/* Warnings */}
              {preview.warnings.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    {t('enrollDialog.preview.warnings', { count: preview.warnings.length })}
                  </div>
                  <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                    {preview.warnings.map((w) => (
                      <li key={w.contact_id} className="text-sm flex items-baseline gap-2">
                        <Badge variant={w.previous_status === 'bounced' ? 'destructive' : 'secondary'} className="text-xs shrink-0">
                          {w.previous_status}
                        </Badge>
                        <span className="text-muted-foreground">
                          {w.contact_name || w.contact_email} &mdash; {w.reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Domains without email, known bounces (#386) */}
              <EnrollScreeningGroups preview={preview} />

              {/* Missing variables */}
              {preview.missing_variables.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3 space-y-1">
                  {preview.missing_variables.map((mv) => (
                    <p key={mv.key} className="text-sm text-amber-700 dark:text-amber-400">
                      {t('enrollDialog.preview.missingVariable', {
                        count: mv.count,
                        variable: `{{${mv.key}}}`,
                      })}
                    </p>
                  ))}
                </div>
              )}

              {/* Contact drill-down: anteprima e override per contatto (#220) */}
              <SegmentContactPicker
                contactSearch={contactSearch}
                overridesByContact={overridesByContact}
                onSelect={setPreviewContact}
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setStep('select')}>
                {t('common:back')}
              </Button>
              {needsConfirmation(preview) ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handleEnroll(false)}
                    disabled={enrollMutation.isPending || preview.ready === 0}
                  >
                    {enrollMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t('enrollDialog.preview.excludeAndProceed', { count: preview.ready })}
                  </Button>
                  <Button
                    onClick={() => handleEnroll(true)}
                    disabled={enrollMutation.isPending}
                  >
                    {enrollMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t('enrollDialog.preview.includeAll', { count: preview.ready_with_force })}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => handleEnroll(false)}
                  disabled={enrollMutation.isPending || preview.ready === 0}
                >
                  {enrollMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('enrollDialog.preview.confirm', { count: preview.ready })}
                </Button>
              )}
            </DialogFooter>
          </>
        )}

        {step === 'result' && enrollResult && (
          <>
            <DialogHeader>
              <DialogTitle>{t('enrollDialog.result.title')}</DialogTitle>
            </DialogHeader>
            <div className="py-6 text-center space-y-2">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
              <p className="text-lg font-medium">
                {t('enrollDialog.result.enrolled', { count: enrollResult.enrolled })}
              </p>
              {enrollResult.skipped > 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('enrollDialog.result.skipped', { count: enrollResult.skipped })}
                </p>
              )}
              {enrollResult.skippedDoNotContact > 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('enrollDialog.result.skippedDoNotContact', { count: enrollResult.skippedDoNotContact })}
                </p>
              )}
              {enrollResult.skippedInvalidDomain > 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('enrollDialog.result.skippedInvalidDomain', { count: enrollResult.skippedInvalidDomain })}
                </p>
              )}
              <EnrollNotesProgress batchId={enrollResult.batchId} />
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>{t('common:close')}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
